import {
  useRef,
  useState,
  useEffect,
  type CSSProperties,
  type ReactNode
} from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { OverlayTriggerProps } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useAppContext } from '@/Main';
import { getColor } from '@/helpers/utils';

type HelpTooltipProps = {
  html?: ReactNode;
  color?: string;
  delayToRemove?: number;
  placement?: OverlayTriggerProps['placement'];
  showOnHover?: boolean;
  icon?: IconProp;
  className?: string;
  styles?: CSSProperties;
  overlayStyle?: CSSProperties;
};

const HelpTooltip = ({
  html,
  color = getColor('gray-1100'),
  delayToRemove = 650,
  placement = 'auto',
  showOnHover = true,
  icon = 'question-circle',
  className = 'ms-1',
  styles = {},
  overlayStyle
}: HelpTooltipProps) => {
  const opacity = color === getColor('gray-1100') ? 0.25 : 0.5;
  const [shouldShow, setShouldShow] = useState(false);
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const {
    config: { isRTL }
  } = useAppContext() as unknown as { config: { isRTL: boolean } };
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current ?? undefined);
    setShouldShow(true);
  };

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current ?? undefined);
    };
  }, []);

  return (
    <OverlayTrigger
      show={Boolean(html) && shouldShow}
      placement={placement}
      overlay={
        <Tooltip
          onClick={e => e.stopPropagation()}
          onMouseEnter={() => {
            if (showOnHover) {
              clearTimeout(timeoutRef.current ?? undefined);
              setIsTooltipHovered(true);
            }
          }}
          onMouseLeave={() => {
            setIsTooltipHovered(false);
            clearTimeout(timeoutRef.current ?? undefined);
            setShouldShow(false);
          }}
          style={{ position: 'fixed', ...overlayStyle }}
          className="text-danger"
        >
          {typeof html === 'string' ? (
            <span dangerouslySetInnerHTML={{ __html: html }} />
          ) : (
            html
          )}
        </Tooltip>
      }
    >
      <span
        className="d-inline-flex align-items-center justify-content-center"
        style={{ height: '1em', lineHeight: 1 }}
      >
        <FontAwesomeIcon
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => {
            setIsTooltipHovered(false);
            clearTimeout(timeoutRef.current ?? undefined);
            timeoutRef.current = setTimeout(() => {
              if (!isTooltipHovered) {
                setShouldShow(false);
              }
            }, delayToRemove);
          }}
          icon={icon}
          transform="shrink-1"
          style={{
            transform: isRTL ? 'scaleX(-1)' : 'scaleX(1)',
            color,
            opacity,
            ...styles
          }}
          className={`${className} cursor-pointer`}
          id="weeklySalesTooltip"
        />
      </span>
    </OverlayTrigger>
  );
};

export default HelpTooltip;
